import { Helper } from "../helpers/helper";
import { ContentItem, ImisResponseItems } from "../models/imis.models";
import { RestUtility } from "./rest.utility";

/**
 * Static helpers for reading and managing iMIS iPart configuration settings, both via the REST API (content item settings) and the DOM-based settings form used in iPart design mode.
 */
export class iPartUtility {
    /**
     * Reads the content key and content item key values from the given DOM elements and retrieves the corresponding iPart settings.
     * @param contentKeySelector A jQuery selector for the input element holding the content key value.
     * @param contentItemKeySelector A jQuery selector for the input element holding the content item key value.
     * @returns The iPart settings object for the identified content item.
     */
    public static async getIpartSettings(contentKeySelector: string, contentItemKeySelector: string): Promise<any> {
        let contentKey = jQuery(contentKeySelector).val().toString();
        let contentItemKey = jQuery(contentItemKeySelector).val().toString();

        return this.getIpartSettingsByKeys(contentKey, contentItemKey);
    }

    /**
     * Retrieves the iPart settings for a content item, handling both cloud (Items array) and non-cloud (Settings property) REST response shapes.
     * @param contentKey The content key of the iPart's containing content.
     * @param contentItemKey The content item key identifying the specific iPart instance.
     * @returns The iPart settings object, or an empty object if no matching content item was found.
     */
    public static async getIpartSettingsByKeys(contentKey: string, contentItemKey: string): Promise<any> {
        let iPartSettings = {};

        let contentItemResponse = await RestUtility.restApiGetData<ImisResponseItems<ContentItem>>("/api/contentitem", { contentKey, contentItemKey });
        if (contentItemResponse.Items == null) {
            // non-cloud
            iPartSettings = (<any>contentItemResponse).Settings;
        } else {
            // cloud
            let contentItem = contentItemResponse.Items[0];
            if (contentItem != null) {
                iPartSettings = contentItem.Data.Settings;
            }
        }

        return iPartSettings;
    }

    /**
     * Defaults the "do not render in design mode" checkbox to checked, and optionally hides the built-in iMIS iPart title panel from the settings form.
     * @param hideDefaultSettings Whether to hide the out-of-the-box iPart title panel settings; defaults to false.
     */
    public static hideAndDefaultImisIpartSettings(hideDefaultSettings: boolean = false) {
        // default the "do not render in design mode" option to true
        let renderInDesignModeInput = document.querySelector<HTMLInputElement>("[id$=_DoNotRenderInDesignMode_CheckBoxSet]");
        renderInDesignModeInput.checked = true;

        // hide all the other out of the box iMIS iPart settings
        if (hideDefaultSettings) {
            document.querySelector<HTMLDivElement>("[id$=_iPartTitlePanel]").style.display = "none";
        }
    }

    /**
     * Sets up two-way synchronization between an iPart's settings form elements and its hidden JSON settings input: collects the distinct setting names, seeds default values, applies any previously-saved JSON settings back onto the form, and wires change handlers to keep the JSON input in sync as the user edits the form.
     * @param settingElementSelector A jQuery selector (or element/element collection) identifying the setting input elements to manage.
     * @returns The initialized settings object, keyed by setting name.
     */
    public static initializeIpartSettingsConfiguration(settingElementSelector: any): any {
        // get all unique setting keys
        let iPartSettings = {};
        let jSettingElements = jQuery<HTMLElement>(settingElementSelector);
        let settingNames: string[] = this.getSettingNames(jSettingElements);

        // set settings to default empty values
        // this populates iPartSettings with the expected setting names
        settingNames.forEach(name => {
            let jSetting = this.getSettingElementsByName(jSettingElements, name);
            this.updateSettingValue(iPartSettings, name, jSetting);
        });

        // get settings from hidden json input if available and sync with setting elements
        let jJsonSettings = this.getJsonSettingsInput();
        if (jJsonSettings.length != 0) {
            let settingsString = jJsonSettings.val().toString();

            if (!Helper.isStringNullOrEmpty(settingsString)) {
                let settingsObject = JSON.parse(settingsString);
                Object.keys(settingsObject).forEach((settingName) => {
                    if (settingName in iPartSettings) {
                        iPartSettings[settingName] = settingsObject[settingName];
                    } else {
                        console.warn(`Setting with name ${settingName} does not match an existing setting element and has been removed`);
                    }
                });

                this.updateSettingElements(iPartSettings, jSettingElements);
            }
        } else {
            console.warn("JSON settings not available on initialization");
        }

        // track settings for changes and update
        settingNames.forEach(name => {
            let jSetting = this.getSettingElementsByName(jSettingElements, name);
            jSetting.on("change", () => {
                this.updateSettingValue(iPartSettings, name, jSetting);
                this.serializeSettings(iPartSettings);
            });
        });

        // initial serialization of settings
        this.serializeSettings(iPartSettings);
        return iPartSettings;
    }

    /**
     * Applies each value in the given settings object back onto its corresponding setting element(s) in the form.
     * @param iPartSettings An object mapping setting names to their current values.
     * @param jSettingElements The full set of setting elements on the form, used to locate the elements for each setting name.
     */
    public static updateSettingElements(iPartSettings: any, jSettingElements: JQuery<HTMLElement>) {
        Object.keys(iPartSettings).forEach((settingName) => {
            let value = iPartSettings[settingName];

            let jSettingNameElements = this.getSettingElementsByName(jSettingElements, settingName);
            if (jSettingNameElements.length != 0) {
                this.setSettingElementValueByType(jSettingNameElements, value);
            } else {
                console.warn("Element for setting does not exist with the following values: ", settingName, value);
            }
        });
    }

    /**
     * Reads the current value from a set of setting elements, handling text/select, radio group, and checkbox inputs differently.
     * @param jSettingElements The jQuery collection of elements sharing a setting name.
     * @returns The current value of the setting: the input's value for text/select elements, the checked radio button's value for radio groups, or a boolean for checkboxes.
     */
    public static getSettingElementValueByType(jSettingElements: JQuery<HTMLElement>) {
        let value;

        if (this.isSettingElementTypeText(jSettingElements)) {
            value = jSettingElements.val();
        } else if (this.isSettingElementTypeRadio(jSettingElements)) {
            // this could possibly involve more than one radio button, filter out the unchecked ones and get the value
            value = jSettingElements.filter(":checked").val();
        } else {
            value = jSettingElements.prop("checked");
        }

        return value;
    }

    /**
     * Writes a value onto a set of setting elements, handling text/select, radio group, and checkbox inputs differently.
     * @param jSettingElements The jQuery collection of elements sharing a setting name.
     * @param newValue The value to apply: a value for text/select elements, the value of the radio button to check for radio groups, or a boolean for checkboxes.
     */
    public static setSettingElementValueByType(jSettingElements: JQuery<HTMLElement>, newValue) {
        // this works for a select tag too because it will be null
        if (this.isSettingElementTypeText(jSettingElements)) {
            jSettingElements.val(newValue);
        } else if (this.isSettingElementTypeRadio(jSettingElements)) {
            // this could possible involve more than one radio element, select the one with the matching value
            jSettingElements.filter(`[value=${newValue}]`).prop("checked", true);
        } else {
            jSettingElements.prop("checked", newValue);
        }
    }

    /**
     * Reads the current value from a setting's elements and stores it on the settings object under the given name.
     * @param iPartSettings The settings object to update.
     * @param settingName The name of the setting to update.
     * @param jSettingElements The jQuery collection of elements for this setting.
     */
    public static updateSettingValue(iPartSettings: any, settingName: string, jSettingElements: JQuery<HTMLElement>) {
        iPartSettings[settingName] = this.getSettingElementValueByType(jSettingElements);
    }

    /**
     * Serializes the given settings object to JSON and writes it into the hidden JSON settings input.
     * @param jsonSettings The settings object to serialize.
     */
    public static serializeSettings(jsonSettings) {
        let input = this.getJsonSettingsInput();

        if (input.length != 0) {
            input.val(JSON.stringify(jsonSettings));
        } else {
            console.warn("json settings not available::", JSON.stringify(jsonSettings));
        }
    }

    /**
     * Retrieves the hidden input element that stores the iPart's settings as a JSON string.
     * @returns The jQuery-wrapped #JsonSettings input element.
     */
    public static getJsonSettingsInput(): JQuery<HTMLInputElement> {
        return jQuery("#JsonSettings");
    }

    /**
     * Filters a collection of setting elements down to those matching the given setting name.
     * @param jSettingElements The full collection of setting elements to filter.
     * @param settingName The name attribute value to match.
     * @returns The subset of elements whose name attribute matches settingName.
     */
    public static getSettingElementsByName(jSettingElements: JQuery<HTMLElement>, settingName: string): JQuery<HTMLElement> {
        return jSettingElements.filter(`[name=${settingName}]`);
    }

    /**
     * Extracts the distinct setting names (from each element's name attribute) present in a collection of setting elements.
     * @param jSettingElements The collection of setting elements to scan.
     * @returns An array of unique setting names, in first-seen order.
     */
    private static getSettingNames(jSettingElements: JQuery<HTMLElement>): string[] {
        let settingKeys: string[] = [];

        jSettingElements.map(function () { return this.getAttribute("name") }).get().forEach(settingKey => {
            if (!settingKeys.includes(settingKey)) {
                settingKeys.push(settingKey);
            }
        });

        return settingKeys;
    }

    /**
     * Determines whether a setting element is a plain text/select-style input, i.e. not a checkbox or radio button.
     * @param jSettingElement The setting element(s) to check.
     * @returns True if the element's type is neither "checkbox" nor "radio".
     */
    private static isSettingElementTypeText(jSettingElement: JQuery<HTMLElement>): boolean {
        let type = jSettingElement.attr("type");
        return !["checkbox", "radio"].includes(type);
    }

    /**
     * Determines whether a setting element is a radio button input.
     * @param jSettingElement The setting element(s) to check.
     * @returns True if the element's type is "radio".
     */
    private static isSettingElementTypeRadio(jSettingElement: JQuery<HTMLElement>): boolean {
        let type = jSettingElement.attr("type");
        return type == "radio";
    }
}